import React, { FunctionComponent } from 'react'
import { ChatbotSparklesIcon } from '../Icons/Icons'
import { Note } from './BriefSectionTypes'
import styles from './BriefShell.module.css'

interface Props {
  note: Note
  expanded: boolean
  onToggle: () => void
  testId?: string
  /** heading above the note; without one the note shows no header */
  title?: string
}

const nonEmptyExplanation = (explanation?: string[]): string[] =>
  Array.isArray(explanation) ? explanation.filter(paragraph => paragraph.trim().length > 0) : []

const renderExplanation = (explanation: string[]) =>
  explanation.map((paragraph, index) => {
    const parts = paragraph.split('**')
    return (
      <p key={index}>
        {parts.map((part, partIndex) => (partIndex % 2 === 1 ? <strong key={partIndex}>{part}</strong> : part))}
      </p>
    )
  })

export const NotesBubble: FunctionComponent<{ children: React.ReactNode; showHeader?: boolean; title?: string }> = ({
  children,
  showHeader = true,
  title = 'Notes',
}) => (
  <>
    {showHeader && (
      <div className={styles.notesHeader}>
        <span className={styles.aiAccent}>
          <ChatbotSparklesIcon />
        </span>
        <h3 className={styles.notesHeading}>{title}</h3>
      </div>
    )}
    <div className={styles.notesBubbleWrapper}>
      <div className={styles.notesArrow}>
        <svg width='10' height='16' viewBox='0 0 10 16'>
          <path d='M10 0 L0 8 L10 16' fill='var(--bubble-bg)' stroke='var(--bubble-border)' strokeWidth='1' />
          <line x1='10' y1='1' x2='10' y2='15' stroke='var(--bubble-bg)' strokeWidth='2' />
        </svg>
      </div>
      <div className={styles.notesBody}>{children}</div>
    </div>
  </>
)

export const renderExpandableExplanation = (
  explanation: string[] | undefined,
  expanded: boolean,
  onToggle: () => void,
  testId: string
): React.ReactNode => {
  const paragraphs = nonEmptyExplanation(explanation)
  return (
    <>
      <div data-testid={testId} className={`${styles.notesExplanation}${!expanded ? ` ${styles.collapsed}` : ''}`}>
        {renderExplanation(paragraphs)}
      </div>
      {paragraphs.length > 0 && (
        <a
          href='#show-more'
          className='action primary'
          aria-expanded={expanded}
          onClick={event => {
            event.preventDefault()
            onToggle()
          }}
        >
          {expanded ? 'Show less...' : 'Show more...'}
        </a>
      )}
    </>
  )
}

export const PinnedNotesCard: FunctionComponent<Props> = ({
  note,
  expanded,
  onToggle,
  testId = 'feature-utilization-note',
  title,
}) => {
  const paragraphs = nonEmptyExplanation(note.explanation)
  if (paragraphs.length === 0) return null

  return (
    <div className={styles.pinnedNotesCard} data-testid={testId}>
      <NotesBubble showHeader={Boolean(title)} title={title}>
        {renderExpandableExplanation(note.explanation, expanded, onToggle, `${testId}-explanation`)}
      </NotesBubble>
    </div>
  )
}
